/**
 * LeadershipBriefingPreview — the hero briefing panel (ported from the v3 landing
 * mockup). A faithful, branded mock of the in-app AviumAIBriefingHero: the
 * teal→amber LEADERSHIP / AI BRIEFING wordmark, the headline, Top 3 + Items to
 * Watch, and the three action buttons. The "answer" beside the graph's "evidence".
 */

const TOP3 = [
  { label: 'Sprint 12:', detail: '14% done · 10/72 items' },
  { label: 'Nick Jones:', detail: '143% over (57/40h)' },
  { label: 'Product velocity:', detail: 'sharp drop (−25%)' },
];
const WATCH = [
  { label: 'Blocked / stalled:', detail: '12 items' },
  { label: 'Dana Kim:', detail: '25% — under-utilized' },
  { label: 'Design velocity:', detail: 'trending up' },
  { label: 'Unassigned:', detail: '13 tickets' },
  { label: 'Unestimated:', detail: '7 items' },
];

function Column({ title, items, accent }: { title: string; items: { label: string; detail: string }[]; accent: string }) {
  return (
    <div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-teal-300 mb-1.5">{title}</div>
      <ol className="space-y-1.5 text-[13px] leading-snug">
        {items.map((p, i) => (
          <li key={i} className="flex gap-2">
            <span className={`${accent} font-bold tabular-nums`}>#{i + 1}</span>
            <div><span className="font-semibold">{p.label}</span> <span className="text-teal-100">{p.detail}</span></div>
          </li>
        ))}
      </ol>
    </div>
  );
}

export default function LeadershipBriefingPreview() {
  return (
    <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-gradient-to-br from-brand-primary-dark via-brand-primary to-brand-spark p-5 text-white h-full flex flex-col">
      <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle,rgba(255,255,255,.4) 1px,transparent 1px)', backgroundSize: '22px 22px' }} />
      <div className="relative flex flex-col flex-1">
        <div className="flex items-center gap-3 mb-2">
          <span className="text-3xl" aria-hidden>✨</span>
          <div>
            <h3 className="font-extrabold text-white text-2xl tracking-tight uppercase leading-none">Leadership</h3>
            <p className="font-semibold text-teal-200 text-[11px] tracking-wider uppercase mt-1">AI Briefing</p>
          </div>
        </div>
        <p className="text-[11px] text-teal-100/85 leading-snug mb-3">
          Analyzing <strong>this sprint&rsquo;s Avium Signals</strong> — sprint risk, blocked work, allocation, and data quality — to surface what needs your attention <strong>today</strong>.
        </p>
        <p className="text-lg font-bold text-white leading-snug mb-3">Sprint 12 is at risk — 14% complete with 7 days left.</p>
        <div className="grid grid-cols-2 gap-4 flex-1 content-start">
          <Column title="Top 3" items={TOP3} accent="text-amber-300" />
          <Column title="Items to watch" items={WATCH} accent="text-teal-300" />
        </div>
        <div className="grid grid-cols-3 gap-2 mt-4">
          {['🎯 Sprint Risk Forecast', '🧹 Data-Quality Fixes', '📝 Draft Sprint Review'].map(b => (
            <span key={b} className="bg-white text-brand-primary-dark text-[11px] font-semibold px-2 py-1.5 rounded-full inline-flex items-center justify-center text-center shadow-sm">{b}</span>
          ))}
        </div>
      </div>
    </div>
  );
}
